import React from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  setCurrentTestResults,
  setFilters,
} from '../../../App/Slices/TestResults';
import { APPLICATIONS, STATUSES } from '../../../Constants/constants';
import { MultiSelect } from 'primereact/multiselect';
import './Filters.css';
import { Button } from 'primereact/button';

export default function Filters({ setShowFilters }) {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.testResults.filters);
  const originalTestResults = useSelector(
    (state) => state.testResults.originalTestResults
  );

  const currentTestResults = useSelector(
    (state) => state.testResults.currentTestResults
  );

  useEffect(() => {
    const source =
      originalTestResults?.length > 0
        ? originalTestResults
        : currentTestResults;
    if (!source || source.length === 0) return;

    const clientSet = new Set();
    const templateSet = new Set();
    const siteEditionSet = new Set();
    const componentSet = new Set();

    source.forEach((test) => {
      const ctx = test.context || {};
      if (ctx.client) clientSet.add(ctx.client);
      if (ctx.template) templateSet.add(ctx.template);
      if (ctx.siteEdition) siteEditionSet.add(ctx.siteEdition);
      if (test.children?.length > 0) {
        test.children.forEach((c) => c?.name && componentSet.add(c.name));
      }
    });

    const filterData = [
      APPLICATIONS,
      [...clientSet].sort(),
      [...templateSet].sort(),
      [...siteEditionSet].sort(),
      STATUSES,
      [...componentSet].sort(),
    ];

    const newFilters =
      filters && filters.length === filterData.length
        ? filters.map((filter, index) => ({
            ...filter,
            data: filterData[index],
          }))
        : filterData.map((data, index) => ({
            name: filters?.[index]?.name || `filter_${index}`,
            // change: explicit check for undefined
            value:
              filters?.[index]?.value !== undefined ? filters[index].value : [],
            data,
          }));

    // check to avoid infinite dispatch
    const changed = JSON.stringify(filters) !== JSON.stringify(newFilters);
    if (changed) {
      dispatch(setFilters(newFilters));
    }
  }, [originalTestResults, currentTestResults, dispatch]);

  useEffect(() => {
    if (!originalTestResults || originalTestResults.length === 0) {
      return;
    }

    const hasActiveFilters = filters?.some(
      (f) => f.value && f.value.length > 0
    );

    if (!hasActiveFilters) {
      dispatch(setCurrentTestResults(originalTestResults));
      return;
    }

    const updatedResults = originalTestResults
      .map((ur) => {
        // first check if the main node complies
        const keepRoot = filterNode(ur);
        // filter children (safely if ur.children is undefined)
        const children = Array.isArray(ur.children) ? ur.children : [];

        const lastFilter = filters?.[filters.length - 1];
        const filteredChildren =
          lastFilter && lastFilter.value && lastFilter.value.length > 0
            ? children.filter((c) => lastFilter.value.includes(c.name))
            : children;

        return keepRoot ? { ...ur, children: filteredChildren } : null;
      })
      .filter(Boolean);

    dispatch(setCurrentTestResults(updatedResults));
  }, [filters, originalTestResults, dispatch]);

  const handleFilterChange = (value, filterName) => {
    const updatedFilters = filters.map((filter) => {
      if (filter.name === filterName) {
        return {
          ...filter,
          value,
        };
      }
      return filter;
    });
    dispatch(setFilters(updatedFilters));
  };

  const filterNode = (node) => {
    if (!node) return false;
    const ctx = node.context || {};

    // apply all filters (except the second to last/last which are status/components)
    for (let index = 0; index < filters.length; index++) {
      const filter = filters[index];
      if (!filter) continue;

      // if the filter corresponds to 'components' we ignore it here because
      // that filter applies to children, not the root node in general
      if (filter.name === 'components' || filter.name === 'component') continue;

      if (filter.name === 'status') {
        // status is handled separately
        if (
          filter.value &&
          filter.value.length > 0 &&
          !filter.value.includes(node.status)
        ) {
          return false;
        }
        continue;
      }

      if (filter.value && filter.value.length > 0) {
        const nodeValue = ctx[filter.name];
        // if the property does not match or does not exist → filter out
        if (!filter.value.includes(nodeValue)) {
          return false;
        }
      }
    }

    return true;
  };

  const clearFilters = () => {
    dispatch(setFilters(filters.map((f) => ({ ...f, value: [] }))));
    dispatch(setCurrentTestResults(originalTestResults));
  };

  return (
    <div className='tr-filters-container p-4'>
      <div className='d-flex justify-content-between'>
        <Button
          icon='pi pi-filter'
          onClick={clearFilters}
          className='p-button-text'
        >
          Reset
        </Button>
        <div
          className='e-card-header-image e-icons e-close filters-close-btn'
          onClick={(e) => {
            setShowFilters(false);
          }}
        />
      </div>
      {/* First row */}
      <div className='' style={{ display: 'flex' }}>
        {filters.slice(0, 3).map((filter) => (
          <div key={filter.name} style={{ flex: 1 }}>
            <MultiSelect
              value={filter.value}
              options={
                filter.data?.map((item) => ({
                  label: item,
                  value: item,
                })) || []
              }
              onChange={(e) => handleFilterChange(e.value, filter.name)}
              placeholder={`Select ${filter.name}`}
              showClear
              style={{ width: '100%' }}
            />
          </div>
        ))}
      </div>

      {/* Second row */}
      <div className='' style={{ display: 'flex' }}>
        {filters.slice(3).map((filter) => (
          <div key={filter.name} style={{ flex: 1 }}>
            <MultiSelect
              value={filter.value}
              options={
                filter.data?.map((item) => ({
                  label: item,
                  value: item,
                })) || []
              }
              onChange={(e) => handleFilterChange(e.value, filter.name)}
              placeholder={`Select ${filter.name}`}
              showClear
              style={{ width: '100%' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
